// src/services/storyViews.js
//
// ÖLÇEK FAZ 1 — Hikaye izlenme kayıt/okuma servisi.
//
// Eski yol: StoryModalMobile/Desktop runTransaction ile hikaye doc'una
// izleyenler:[...viewers, me] (TÜM array yeniden yazılıyordu) +
// viewersCount:n+1 yazıyordu. Sorunlar:
//   - ~36K izleyicide hikaye doc'u 1MB tavanına çarpar (kalıcı kırılma).
//   - Tek doc'a sürekli yazma ~1/sn → transaction retry fırtınası.
//   - Her izlenme yazması, doc'a abone TÜM dinleyicilere (tray + modal)
//     büyüyen izleyenler[] dahil tam doc'u yeniden gönderiyordu
//     (okuma faturası: dinleyici × yazma çarpımı).
//
// Yeni yol (bu modül):
//   hikayeler/{id}/izleyenler/{viewerUid}     { viewedAt }   ← izleyici-başına doc
//   users/{me}/izlenenHikayeler/{storyId}     { authorId, viewedAt, expireAt }
//
// Story doc'u create sonrası immutable kalır; izlendi-senkronu kullanıcının
// kendi alt koleksiyonundan gelir, başkalarının izlemeleri seni tetiklemez.
// Geçiş: 24h pencerede legacy izleyenler[]/viewersCount okumada OR'lanır.

import { auth, db } from "../firebase";
import {
  collection,
  doc,
  getCountFromServer,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  where,
} from "firebase/firestore";

export const SEEN_TTL_MS = 48 * 60 * 60 * 1000; // izlenenHikayeler TTL (hikaye 24h + pay)
const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * `markStoryViewed`'ın hikaye nesnesinden OKUDUĞU alanlar (hikayenin tamamı
 * değil). Alanlar isteğe bağlı çünkü gövde `!story?.id` ve `story.authorId &&`
 * guard'larıyla eksik veriye karşı zaten korunuyor.
 */
export type StoryViewTarget = {
  id?: string | null;
  authorId?: string | null;
};

/**
 * `fetchOwnerViewCount`'un okuduğu alanlar. `izleyenler`/`viewersCount`
 * LEGACY (24h geçiş penceresi) — yeni yazıcılar üretmez, bu yüzden isteğe bağlı.
 */
export type StoryViewCountSource = {
  id?: string | null;
  /** Gövde bu dizinin YALNIZ `.length`ini okur → eleman tipi bağlayıcı değil
   *  (üretici `Hikayeler.HikayeOgesi` `unknown[]` diyor). */
  izleyenler?: unknown[] | null;
  viewersCount?: number | null;
};

/**
 * İzlenmeyi kaydet — idempotent (docId = uid; yeniden izleme overwrite).
 * İki bağımsız setDoc: biri başarısız olsa diğeri yaşar (batch kullanmıyoruz;
 * izleyenler yazımı rules geçişi sırasında reddedilirse izlendi-senkronu
 * yine de çalışsın).
 */
export async function markStoryViewed(story: StoryViewTarget): Promise<void> {
  const me = auth.currentUser;
  if (!me || !story?.id) return;
  if (story.authorId && story.authorId === me.uid) return; // kendi hikayen sayılmaz

  const viewerWrite = setDoc(
    doc(db, "hikayeler", story.id, "izleyenler", me.uid),
    { viewedAt: serverTimestamp() },
  ).catch((e) => console.warn("[storyViews] izleyenler yazılamadı:", e?.code));

  const seenWrite = setDoc(
    doc(db, "users", me.uid, "izlenenHikayeler", story.id),
    {
      authorId: String(story.authorId || ""),
      viewedAt: serverTimestamp(),
      expireAt: Timestamp.fromMillis(Date.now() + SEEN_TTL_MS),
    },
  ).catch((e) => console.warn("[storyViews] izlenenHikayeler yazılamadı:", e?.code));

  await Promise.all([viewerWrite, seenWrite]);
}

/**
 * Sahip için izlenme sayısı: subcollection count (1000 kayda 1 okuma) +
 * legacy taban (24h geçişte eski sekmeler array'e yazmış olabilir).
 * Çifte sayım geçiş penceresinde mümkün ve kabul edilmiş — kendiliğinden söner.
 */
export async function fetchOwnerViewCount(
  story: StoryViewCountSource,
): Promise<number> {
  if (!story?.id) return 0;
  const legacy = Math.max(
    Number(story.viewersCount) || 0,
    Array.isArray(story.izleyenler) ? story.izleyenler.length : 0,
  );
  try {
    const agg = await getCountFromServer(
      collection(db, "hikayeler", story.id, "izleyenler"),
    );
    return legacy + (agg.data().count || 0);
  } catch {
    return legacy;
  }
}

/**
 * Sahip için izleyici listesi sorgusu (StoryViewersSheet kullanır).
 * Ters kronolojik, limit caller'da.
 */
export function viewersCollectionRef(storyId: string) {
  return collection(db, "hikayeler", String(storyId), "izleyenler");
}

export function viewersQuery(storyId: string) {
  return query(viewersCollectionRef(storyId), orderBy("viewedAt", "desc"));
}

/**
 * Cihazlar arası "izlendi" senkronu: kullanıcının kendi izlenenHikayeler
 * alt koleksiyonunu canlı izle (son 24h). Maliyet O(kendi izleme sayın);
 * başkalarının izlemeleri bu listener'ı asla tetiklemez.
 *
 * @param cb storyId set'i
 * @returns unsubscribe
 */
export function watchSeenStories(
  uid: string | null | undefined,
  cb: (seen: Set<string>) => void,
): () => void {
  if (!uid) {
    cb(new Set());
    return () => {};
  }
  const q = query(
    collection(db, "users", uid, "izlenenHikayeler"),
    where("viewedAt", ">=", Timestamp.fromMillis(Date.now() - DAY_MS)),
  );
  return onSnapshot(
    q,
    (snap) => cb(new Set(snap.docs.map((d) => d.id))),
    // Geçici listener hatasında önceki set'i KORU (boşaltma, izlenmiş
    // halkalar yeniden "izlenmemiş" parlamasın) — no-op.
    () => {},
  );
}

const api = {
  markStoryViewed,
  fetchOwnerViewCount,
  viewersCollectionRef,
  viewersQuery,
  watchSeenStories,
  SEEN_TTL_MS,
};
export default api;
